// Region kits (12 §8): the rules that turn logical kinds into Oryx frames. Each kit is data;
// the engine in tiler.ts does the neighbour work. Names follow the layout generator's kit keys
// (crates/fk-forge/src/layout.rs `LayoutStyle::kits`).
import { DUNGEON_ROWS, FILLS, GROUND, LIQUID, PROPS, type DungeonKitRow } from './oryx';
import type { DecalRule, Kit, Variants } from './types';

const one = (f: number): Variants => [[f, 1]];
const even = (fs: readonly number[]): Variants => fs.map((f) => [f, 1] as const);

/** Dark floor set of a dungeon row: plain (heavy) and cracked. The framed and grate pieces read as
 *  plates/crates at board scale, so they are left to props. */
function darkFloor(row: DungeonKitRow): Variants {
  return [[row.base + 3, 14], [row.base + 5, 4]];
}

const cobwebs: DecalRule[] = [
  { on: ['floor'], chance: 0.4, match: { n: 'wall', w: 'wall' }, frames: one(PROPS.cobweb.nw) },
  { on: ['floor'], chance: 0.4, match: { n: 'wall', e: 'wall' }, frames: one(PROPS.cobweb.ne) },
  { on: ['floor'], chance: 0.2, match: { s: 'wall', w: 'wall' }, frames: one(PROPS.cobweb.sw) },
  { on: ['floor'], chance: 0.2, match: { s: 'wall', e: 'wall' }, frames: one(PROPS.cobweb.se) },
];
const clutter = (bones: number, blood: number): DecalRule[] => [
  { on: ['floor'], chance: bones, frames: even([...PROPS.bones, ...PROPS.bonePiles]) },
  { on: ['floor'], chance: blood, frames: even(PROPS.blood) },
];
const rubble = (frames: readonly number[], chance = 0.7): { kind: 'variants'; frames: Variants; decals: Variants; decalChance: number } =>
  ({ kind: 'variants', frames: [], decals: even(frames), decalChance: chance });

function dungeon(key: string, name: string, row: DungeonKitRow, opts: { floor?: Variants; door?: [number, number]; decals?: DecalRule[]; rock?: number } = {}): Kit {
  return {
    key, name,
    base: one(opts.rock ?? FILLS.voidDither),   // 836 is a 19 %-opaque dither: the scene background shows through as the dark
    floor: opts.floor ?? darkFloor(row),
    floorExit: row.stairs ? { stairs: row.base + 7 } : undefined,
    rough: { kind: 'variants', frames: [], decals: even([PROPS.rubble, PROPS.rocks, PROPS.boulder, PROPS.bonePiles[0]]), decalChance: 0.85 },
    water: { set: LIQUID.water!, animate: true },
    wall: { kind: 'oryx16', row, cracked: true, torches: true },
    rock: one(opts.rock ?? FILLS.voidDither),
    door: { closed: opts.door?.[0] ?? PROPS.doors.wood, open: opts.door?.[1] ?? PROPS.doors.archOpen },
    shadows: true,
    decals: opts.decals ?? [...cobwebs, ...clutter(0.03, 0.015)],
  };
}

export const KITS: Record<string, Kit> = {
  // ---- dungeons ----
  keep: dungeon('keep', 'The keep (grey stone)', DUNGEON_ROWS.grey!, { door: [PROPS.doors.wood, PROPS.doors.archOpen] }),
  cistern: dungeon('cistern', 'The Weeping Cistern (teal stone)', DUNGEON_ROWS.teal!, {
    door: [PROPS.doors.iceClosed, PROPS.doors.iceOpen],
    decals: [...cobwebs, ...clutter(0.02, 0.01), { on: ['floor'], chance: 0.05, frames: even(PROPS.puddle) }],
  }),
  crypt: dungeon('crypt', 'Crypt (blue-grey stone)', DUNGEON_ROWS.blueGrey!, {
    door: [PROPS.doors.ironClosed, PROPS.doors.ironOpen],
    decals: [...cobwebs, ...clutter(0.06, 0.02), { on: ['floor'], chance: 0.01, frames: even([PROPS.skullPile, PROPS.skull]) }],
  }),
  sewer: dungeon('sewer', 'Sewer (olive stone)', DUNGEON_ROWS.olive!, {
    door: [PROPS.doors.portcullis, PROPS.doors.gateOpen],
    decals: [...cobwebs, { on: ['floor'], chance: 0.06, frames: even(PROPS.slime) }, { on: ['floor'], chance: 0.03, frames: even(PROPS.puddle) }],
  }),
  brick: dungeon('brick', 'Brick cellar', DUNGEON_ROWS.brick!, { door: [PROPS.doors.wood2, PROPS.doors.archOpen] }),
  shrine: dungeon('shrine', 'Mossy shrine', DUNGEON_ROWS.mossy!, {
    door: [PROPS.doors.blue, PROPS.doors.archOpen],
    decals: [...cobwebs, { on: ['floor'], chance: 0.03, frames: even(PROPS.flowers) }, ...clutter(0.01, 0)],
  }),
  // the row's bevelled flagstones (+0..+2) are the wall tops' own block, so a floor of them read as
  // solid wall; this row's +3 is a checker and +5 a wooden panel: plain dark stone, now and then a checker
  tomb: dungeon('tomb', 'Tomb (dark stone)', DUNGEON_ROWS.darkGrey!, {
    floor: [[DUNGEON_ROWS.darkGrey!.base + 6, 14], [DUNGEON_ROWS.darkGrey!.base + 3, 2]], door: [PROPS.doors.ironClosed, PROPS.doors.ironOpen],
    decals: [...cobwebs, ...clutter(0.08, 0.02)],
  }),
  // docs/57: a desert temple or ruin for puzzle encounters (sandstone)
  temple: dungeon('temple', 'Sandstone temple', DUNGEON_ROWS.tan!, {
    door: [PROPS.doors.wood2, PROPS.doors.archOpen],
    decals: [...cobwebs, ...clutter(0.02, 0), { on: ['floor'], chance: 0.02, frames: even([PROPS.hornedSkull, PROPS.bones[1]]) }],
  }),
  cavern: {
    ...dungeon('cavern', 'Cavern (brown rock)', DUNGEON_ROWS.brownGrey!, { door: [PROPS.doors.boarded[0], PROPS.doors.archOpen], rock: FILLS.greyRock }),
    rough: rubble([PROPS.rocks, PROPS.boulder, PROPS.crystals.grey[0], PROPS.crystals.grey[1]]),
    decals: [{ on: ['floor'], chance: 0.02, frames: even([PROPS.crystals.orange, PROPS.crystals.grey[0]]) }, ...clutter(0.02, 0)],
  },
  ember: {
    key: 'ember', name: 'Ember cavern (orange rock)',
    base: one(FILLS.brownTex),
    floor: [[803, 12], [805, 4], [804, 1]],
    rough: rubble([PROPS.rocks, PROPS.boulder, PROPS.crystals.orange], 0.8),
    water: { set: LIQUID.lava!, animate: true },       // the rules read an ember cave's water as lava (field.rs Land::Ember)
    wall: { kind: 'oryx16', row: DUNGEON_ROWS.cliff!, cracked: true },
    rock: one(FILLS.brownTex),
    door: { closed: PROPS.doors.boarded[1], open: PROPS.doors.archOpen },
    shadows: true,
    decals: [{ on: ['floor'], chance: 0.03, frames: even([PROPS.crystals.orange]) }, { on: ['floor'], chance: 0.02, frames: even(PROPS.bonePiles) }],
  },
  // ---- outdoors ----
  // Ground kits draw the turf as full fills and put the lips on everything else (oryx.ts: one
  // side of the boundary only). Decoration is seeded: `cluster` rules fire only inside seeded
  // patches, so flowers come in meadows, leaves in drifts and pines in groves; `fill` rules swap
  // the fill frame itself (a replacement tile), the rest are transparent overlays or props.
  mirewood: {
    key: 'mirewood', name: 'Mirewood bog',
    base: one(LIQUID.mud!.base + 3 * 57),           // the mud fill shows through under the turf lips
    ground: { set: GROUND.olive!, on: ['floor'] },
    floor: [],
    rough: { kind: 'liquid', set: LIQUID.mud!, animate: true },
    water: { set: LIQUID.water!, animate: true },
    wall: {
      kind: 'props', frames: [[PROPS.bushDense[0], 6], [PROPS.bushSmall[0], 2], [PROPS.bushSmall[1], 2], [PROPS.trees.dead, 1]], anim: true,
      groves: [{ patch: { size: 3, share: 0.3 }, frames: [[PROPS.trees.dead, 5], [PROPS.bushDense[0], 2], [PROPS.trees.pineSingle[1], 1]] }],
    },
    rock: one(LIQUID.mud!.base + 3 * 57),
    thicket: [[PROPS.bushDense[0], 4], [PROPS.starPlant[1], 2], [PROPS.bushSmall[2], 2]],
    rockAsWall: true,
    door: { closed: PROPS.doors.boarded[0], open: PROPS.doors.archOpen },
    decals: [
      { on: ['water'], chance: 0.1, frames: even(PROPS.lilyPad), layer: 'props' },
      { on: ['rough'], chance: 0.06, frames: even(PROPS.bushSmall), layer: 'props' },
      { on: ['floor'], chance: 0.3, cluster: { size: 3, share: 0.3 }, frames: [[PROPS.starPlant[0], 1], [PROPS.starPlant[1], 2]] },   // teal plants in clumps
      { on: ['floor'], chance: 0.2, cluster: { size: 3, share: 0.25 }, frames: even(PROPS.puddle) },                                     // boggy puddles
      { on: ['floor'], chance: 0.04, frames: even(PROPS.flowers) },
      { on: ['floor'], chance: 0.03, frames: even(PROPS.shrubs.teal) },
      { on: ['floor'], chance: 0.012, frames: even(PROPS.rocksBrown) },
    ],
  },
  forest: {
    key: 'forest', name: 'Greenwood',
    base: one(FILLS.dirt),
    ground: { set: GROUND.grass!, on: ['floor', 'wall'] },
    floor: [],
    // undergrowth: green bushes and shrubs on a bare patch; the orange brambles read as fire on
    // dirt at board scale and belong to the autumn kit
    rough: { kind: 'variants', frames: [], decals: [[PROPS.bushDense[0], 3], [PROPS.bushSmall[0], 3], [PROPS.bushSmall[1], 3], [PROPS.bushSmall[2], 2], [PROPS.shrubs.green[0], 2], [PROPS.shrubs.green[1], 2], [PROPS.shrubs.green[2], 2]], decalChance: 1 },
    water: { set: LIQUID.water!, animate: true },
    wall: {
      kind: 'props', frames: [[PROPS.trees.oakSingle[0], 4], [PROPS.trees.oakSingle[1], 4], [PROPS.trees.oakSingle[2], 4], [PROPS.trees.oak, 2], [PROPS.trees.fruit, 1]],
      groves: [{ patch: { size: 4, share: 0.25 }, frames: [[PROPS.trees.pineSingle[0], 3], [PROPS.trees.pineSingle[1], 3], [PROPS.trees.pineSingle[2], 3], [PROPS.trees.pine, 2], [PROPS.trees.oakSingle[1], 1]] }],   // pine stands
    },
    rock: one(FILLS.dirt),
    thicket: [[PROPS.bushDense[0], 5], [PROPS.roundBush[0], 2], [PROPS.roundBush[1], 2]],
    rockAsWall: true,
    door: { closed: PROPS.doors.boarded[0], open: PROPS.doors.archOpen },
    decals: [
      { on: ['floor'], chance: 0.5, cluster: { size: 3, share: 0.12 }, frames: one(FILLS.tallGrass), layer: 'fill' },                   // tall-grass patches (replacement fill), ragged
      { on: ['floor'], chance: 0.35, cluster: { size: 4, share: 0.3 }, frames: [[PROPS.flowers[0], 1], [PROPS.flowers[1], 2], [PROPS.flowers[2], 2]] },   // meadows
      { on: ['floor'], chance: 0.015, frames: even(PROPS.flowers) },
      { on: ['floor'], chance: 0.03, frames: even(PROPS.shrubs.green) },
      { on: ['floor'], chance: 0.03, frames: even([PROPS.leaves[0], PROPS.leaves[1]]) },
      { on: ['floor'], chance: 0.012, frames: even([PROPS.trees.stump, PROPS.rocks, ...PROPS.rocksWhite]) },
      { on: ['water'], chance: 0.06, frames: even(PROPS.lilyPad), layer: 'props' },
    ],
  },
  autumn: {
    key: 'autumn', name: 'Autumn wood',
    base: one(FILLS.dirt),
    ground: { set: GROUND.olive!, on: ['floor', 'wall'] },
    floor: [],
    rough: { kind: 'variants', frames: [], decals: even([...PROPS.trees.bramble, ...PROPS.tallGrassOrange]), decalChance: 0.9 },
    water: { set: LIQUID.water!, animate: true },
    wall: {
      kind: 'props', frames: [[PROPS.trees.autumnSingle[0], 4], [PROPS.trees.autumnSingle[1], 4], [PROPS.trees.autumnSingle[2], 4], [PROPS.trees.autumn, 2], [PROPS.trees.dead, 1]],
      groves: [{ patch: { size: 4, share: 0.2 }, frames: [[PROPS.trees.dead, 4], [PROPS.trees.autumnSingle[2], 2], [PROPS.trees.pineSingle[0], 1]] }],   // dead stands
    },
    rock: one(FILLS.dirt),
    thicket: [[PROPS.bushDenseOrange[0], 4], [PROPS.trees.bramble[0], 2], [PROPS.trees.bramble[1], 2], [PROPS.roundBushBrown[0], 2]],
    rockAsWall: true,
    door: { closed: PROPS.doors.boarded[0], open: PROPS.doors.archOpen },
    decals: [
      { on: ['floor'], chance: 0.3, cluster: { size: 3, share: 0.3 }, frames: [[PROPS.leaves[2], 1], [PROPS.leaves[3], 2]] },          // leaf drifts
      { on: ['floor'], chance: 0.4, cluster: { size: 3, share: 0.15 }, frames: even(PROPS.tallGrassOrange) },                        // tall-grass patches
      { on: ['floor'], chance: 0.03, frames: even(PROPS.leaves) },
      { on: ['floor'], chance: 0.04, frames: even(PROPS.shrubs.orange) },
      { on: ['floor'], chance: 0.03, frames: even(PROPS.shrubs.tufts) },
      { on: ['floor'], chance: 0.02, frames: even(PROPS.bushSmallOrange) },
      { on: ['floor'], chance: 0.01, frames: even([PROPS.trees.stump, ...PROPS.rocksBrown]) },
    ],
  },
  // ---- docs/23 biome kits: the module draws forest layouts with all five (and the underdark and ashlands) and bog layouts with
  // jungle (and mirewood). Each keeps one side of every ground boundary (lips), like the woods above.
  winterwood: {
    key: 'winterwood', name: 'Winterwood (snowfield)',
    base: one(FILLS.tan),                              // frozen earth shows where the snow lips meet scrub
    ground: { set: GROUND.snow!, on: ['floor', 'wall'] },
    floor: [],
    // scrub and stones poking out of the snow on a patch of bare frozen ground
    rough: { kind: 'variants', frames: [], decals: [[PROPS.shrubs.tufts[0], 2], [PROPS.shrubs.tufts[1], 2], [PROPS.shrubs.tufts[2], 2], [PROPS.rocks, 2], [PROPS.trees.stump, 1]], decalChance: 0.4 },
    water: { set: LIQUID.water!, animate: true },
    wall: {
      kind: 'props', frames: [[PROPS.trees.snowPineSingle[0], 4], [PROPS.trees.snowPineSingle[1], 4], [PROPS.trees.snowPineSingle[2], 4], [PROPS.trees.snowPineSingle2[1], 3], [PROPS.trees.snowPine, 3], [PROPS.trees.dead, 1]],
      groves: [{ patch: { size: 4, share: 0.22 }, frames: [[PROPS.trees.dead, 4], [PROPS.rocksWhite[0], 2], [PROPS.rocksWhite[2], 2], [PROPS.trees.snowPine, 1]] }],   // bare stands and white crags
    },
    rock: one(FILLS.tan),
    thicket: [[PROPS.roundBushBrown[0], 3], [PROPS.roundBushBrown[1], 3], [PROPS.trees.bramble[2], 1]],
    rockAsWall: true,
    door: { closed: PROPS.doors.boarded[0], open: PROPS.doors.archOpen },
    decals: [
      { on: ['floor'], chance: 0.5, cluster: { size: 3, share: 0.3 }, frames: one(FILLS.snow), layer: 'fill' },                         // wind-ruffled snow
      { on: ['floor'], chance: 0.25, cluster: { size: 3, share: 0.14 }, frames: [[PROPS.rocks, 2], [PROPS.shrubs.tufts[0], 1], [PROPS.shrubs.tufts[2], 1]] },   // stones and grass showing through
      { on: ['floor'], chance: 0.035, frames: even(PROPS.shrubs.tufts) },                                                             // dry grass poking out
      { on: ['floor'], chance: 0.012, frames: even([PROPS.trees.stump, PROPS.rocks]) },
      { on: ['floor'], chance: 0.004, frames: even([PROPS.bones[0], PROPS.hornedSkull]) },
    ],
  },
  pinewood: {
    key: 'pinewood', name: 'Pinewood (taiga)',
    base: one(FILLS.dirt),
    ground: { set: GROUND.darkGreen!, on: ['floor', 'wall'] },
    floor: [],
    // ferny undergrowth
    rough: { kind: 'variants', frames: [], decals: [[PROPS.bushSmall[2], 4], [PROPS.bushSmall[0], 2], [PROPS.leaves[0], 2], [PROPS.leaves[1], 2], [PROPS.rocks, 1]], decalChance: 0.5 },
    water: { set: LIQUID.water!, animate: true },
    wall: {
      kind: 'props', frames: [[PROPS.trees.pineSingle[0], 4], [PROPS.trees.pineSingle[1], 4], [PROPS.trees.pineSingle[2], 4], [PROPS.trees.pine, 3], [PROPS.trees.dead, 1]],
      groves: [{ patch: { size: 4, share: 0.18 }, frames: [[PROPS.rocksWhite[0], 2], [PROPS.rocksWhite[1], 2], [PROPS.rocksWhite[2], 2], [PROPS.trees.pine, 2], [PROPS.boulder, 1]] }],   // granite outcrops
    },
    rock: one(FILLS.dirt),
    thicket: [[PROPS.bushDense[0], 3], [PROPS.bushSmall[2], 3], [PROPS.roundBush[1], 2]],
    rockAsWall: true,
    door: { closed: PROPS.doors.boarded[0], open: PROPS.doors.archOpen },
    decals: [
      { on: ['floor'], chance: 0.3, cluster: { size: 3, share: 0.28 }, frames: [[PROPS.bushSmall[2], 3], [PROPS.leaves[0], 2], [PROPS.leaves[1], 2]] },   // undergrowth and needle litter
      { on: ['floor'], chance: 0.2, cluster: { size: 3, share: 0.14 }, frames: [[PROPS.rocksDarkGrey[0], 1], [PROPS.rocks, 2]] },                          // stony ground
      { on: ['floor'], chance: 0.03, frames: even([PROPS.leaves[0], PROPS.leaves[1]]) },
      { on: ['floor'], chance: 0.012, frames: even(PROPS.flowers) },
      { on: ['floor'], chance: 0.015, frames: even([PROPS.trees.stump, PROPS.rocks]) },
      { on: ['water'], chance: 0.04, frames: even(PROPS.lilyPad), layer: 'props' },
    ],
  },
  dunes: {
    key: 'dunes', name: 'The Dunes (desert)',
    base: one(FILLS.orangeDirt),                       // hard red ground under the sand
    ground: { set: GROUND.peach!, on: ['floor', 'wall'] },
    floor: [],
    // stony hardpan: rocks, dry bushes, bleached bones
    rough: { kind: 'variants', frames: [], decals: [[PROPS.rocks, 4], [PROPS.bones[1], 1], [PROPS.bones[3], 1]], decalChance: 0.3 },
    water: { set: LIQUID.water!, animate: true },      // an oasis
    wall: {
      kind: 'props', frames: [[PROPS.cacti[0], 4], [PROPS.cacti[1], 4], [PROPS.cacti[2], 3], [PROPS.trees.dead, 2]],
      groves: [{ patch: { size: 4, share: 0.3 }, frames: [[PROPS.rocksOrange[0], 3], [PROPS.rocksOrange[1], 3], [PROPS.rocksOrange[2], 3], [PROPS.trees.dead, 1]] }],   // red rock outcrops
    },
    rock: one(FILLS.orangeDirt),
    thicket: [[PROPS.roundBushBrown[0], 3], [PROPS.roundBushBrown[1], 3], [PROPS.roundBush[1], 1]],
    rockAsWall: true,
    door: { closed: PROPS.doors.boarded[1], open: PROPS.doors.archOpen },
    decals: [
      // the oasis: green at the water's edge, lilies on it
      { on: ['water'], chance: 0.14, frames: even(PROPS.lilyPad), layer: 'props' },
      { on: ['floor'], chance: 0.55, match: { n: 'water' }, frames: [[PROPS.roundBush[0], 2], [PROPS.roundBush[1], 3], [PROPS.shrubs.green[1], 2]] },
      { on: ['floor'], chance: 0.55, match: { s: 'water' }, frames: [[PROPS.roundBush[1], 3], [PROPS.shrubs.green[1], 2], [PROPS.flowers[0], 1]] },
      { on: ['floor'], chance: 0.45, match: { e: 'water' }, frames: [[PROPS.roundBush[1], 2], [PROPS.shrubs.green[0], 2]] },
      { on: ['floor'], chance: 0.45, match: { w: 'water' }, frames: [[PROPS.roundBush[1], 2], [PROPS.shrubs.green[2], 2]] },
      { on: ['floor'], chance: 0.45, cluster: { size: 3, share: 0.3 }, frames: one(GROUND.peach2!.base), layer: 'fill' },          // smooth wind-packed sand
      { on: ['floor'], chance: 0.15, cluster: { size: 3, share: 0.14 }, frames: [[PROPS.rocks, 2], [PROPS.bones[1], 1]] },                // stony patches
      { on: ['floor'], chance: 0.008, frames: even([PROPS.hornedSkull, PROPS.bones[0], PROPS.bones[2]]) },
    ],
  },
  highland: {
    key: 'highland', name: 'Highland moor',
    base: one(FILLS.dirt),                             // bare earth where the turf breaks
    ground: { set: GROUND.tan!, on: ['floor', 'wall'] },
    floor: [],
    rough: { kind: 'variants', frames: [], decals: [[PROPS.rocks, 4], [PROPS.shrubs.tufts[0], 2], [PROPS.shrubs.tufts[2], 2]], decalChance: 0.45 },   // scree
    water: { set: LIQUID.water!, animate: true },      // tarns
    wall: {
      // crags among scattered pines and boulders; seeded stands of pine break up the rock (no orange
      // brush: at board scale it reads as fire on the moor)
      kind: 'props', frames: [[PROPS.rocksWhite[0], 3], [PROPS.rocksWhite[1], 3], [PROPS.rocksWhite[2], 3], [PROPS.trees.pineSingle[0], 2], [PROPS.trees.pineSingle[1], 2], [PROPS.trees.pineSingle[2], 2], [PROPS.boulder, 2]],
      groves: [
        { patch: { size: 4, share: 0.3 }, frames: [[PROPS.trees.pineSingle[0], 3], [PROPS.trees.pineSingle[2], 3], [PROPS.trees.pine, 2], [PROPS.rocksWhite[1], 1]] },   // a stand of pines
        { patch: { size: 3, share: 0.15 }, frames: [[PROPS.boulder, 3], [PROPS.rocks, 1], [PROPS.trees.pineSingle[1], 1]] },                                             // a boulder field
      ],
    },
    rock: one(FILLS.dirt),
    thicket: [[PROPS.roundBushBrown[0], 2], [PROPS.roundBush[0], 2], [PROPS.hedges.green[1], 2]],
    rockAsWall: true,
    door: { closed: PROPS.doors.boarded[0], open: PROPS.doors.archOpen },
    decals: [
      { on: ['floor'], chance: 0.45, cluster: { size: 3, share: 0.3 }, frames: [[PROPS.shrubs.tufts[0], 2], [PROPS.shrubs.tufts[1], 2], [PROPS.shrubs.tufts[2], 2]] },   // heather and bracken
      { on: ['floor'], chance: 0.03, frames: one(PROPS.rocks) },
      { on: ['floor'], chance: 0.012, frames: even(PROPS.flowers) },
      { on: ['floor'], chance: 0.006, frames: even([PROPS.rocks, PROPS.hornedSkull]) },
    ],
  },
  jungle: {
    key: 'jungle', name: 'Jungle',
    base: one(LIQUID.mud!.base + 3 * 57),             // mud shows through under the turf lips
    ground: { set: GROUND.darkGreen!, on: ['floor', 'wall'] },
    floor: [],
    rough: { kind: 'liquid', set: LIQUID.mud!, animate: true },
    water: { set: LIQUID.water!, animate: true },
    wall: {
      kind: 'props', frames: [[PROPS.bushDense[0], 5], [PROPS.roundBush[0], 3], [PROPS.trees.fruit, 2], [PROPS.trees.oak, 2], [PROPS.trees.oakSingle[0], 1], [PROPS.trees.oakSingle[2], 1]], anim: true,
      groves: [{ patch: { size: 3, share: 0.3 }, frames: [[PROPS.trees.fruit, 4], [PROPS.trees.oak, 2], [PROPS.bushDense[0], 2]] }],   // fruiting canopy
    },
    rock: one(LIQUID.mud!.base + 3 * 57),
    thicket: [[PROPS.bushDense[0], 5], [PROPS.roundBush[0], 3], [PROPS.starPlant[0], 2]],
    rockAsWall: true,
    door: { closed: PROPS.doors.boarded[0], open: PROPS.doors.archOpen },
    decals: [
      { on: ['water'], chance: 0.2, frames: even(PROPS.lilyPad), layer: 'props' },
      { on: ['rough'], chance: 0.08, frames: even([PROPS.lilyPad[1], PROPS.bushSmall[2]]), layer: 'props' },
      { on: ['floor'], chance: 0.4, cluster: { size: 3, share: 0.32 }, frames: [[PROPS.starPlant[0], 2], [PROPS.starPlant[1], 2]] },     // fronds
      { on: ['floor'], chance: 0.3, cluster: { size: 3, share: 0.2 }, frames: [[PROPS.bushSmall[0], 1], [PROPS.bushSmall[1], 1], [PROPS.bushSmall[2], 2], [PROPS.leaves[0], 1]] },   // understorey
      { on: ['floor'], chance: 0.04, frames: even(PROPS.flowers) },
      { on: ['floor'], chance: 0.02, frames: even(PROPS.puddle) },
    ],
  },
  // docs/23 Y9: the Underdark's open country, drawn with forest layouts like the outdoor kits but
  // on cave floor: stone pillars and crystal for its walls, teal growth (the glowing fungus, Oryx
  // has no mushrooms) in stands and thickets, black pools
  underdark: {
    key: 'underdark', name: 'The Underdark (fungus caverns)',
    base: one(FILLS.greyRock),
    floor: darkFloor(DUNGEON_ROWS.brownGrey!),
    rough: rubble([PROPS.rocks, PROPS.boulder, PROPS.crystals.grey[0], PROPS.crystals.grey[1], ...PROPS.rocksDarkGrey.slice(0, 2)]),
    water: { set: LIQUID.water!, animate: true },
    wall: {
      kind: 'props', frames: [[PROPS.rocksDarkGrey[0], 3], [PROPS.rocksDarkGrey[2], 3], [PROPS.rocksDarkGrey[4], 3], [PROPS.boulder, 2], [PROPS.starPlant[1], 2], [PROPS.crystals.grey[0], 1]],
      groves: [
        { patch: { size: 3, share: 0.3 }, frames: [[PROPS.starPlant[0], 3], [PROPS.starPlant[1], 3], [PROPS.shrubs.teal[1], 2], [PROPS.rocksDarkGrey[1], 1]] },   // a fungus stand
        { patch: { size: 3, share: 0.12 }, frames: [[PROPS.crystals.grey[0], 2], [PROPS.crystals.grey[1], 2], [PROPS.crystals.orange, 1]] },                       // a crystal grotto
      ],
    },
    rock: one(FILLS.greyRock),
    thicket: [[PROPS.starPlant[0], 3], [PROPS.starPlant[1], 3], [PROPS.shrubs.teal[0], 2], [PROPS.shrubs.teal[2], 2]],
    rockAsWall: true,
    door: { closed: PROPS.doors.boarded[0], open: PROPS.doors.archOpen },
    shadows: true,
    decals: [
      { on: ['floor'], chance: 0.35, cluster: { size: 3, share: 0.25 }, frames: [[PROPS.shrubs.teal[0], 2], [PROPS.shrubs.teal[1], 1], [PROPS.shrubs.teal[2], 2]] },   // lichen and glowcap patches
      { on: ['floor'], chance: 0.2, cluster: { size: 3, share: 0.15 }, frames: [[PROPS.rocks, 2], [PROPS.rocksDarkGrey[3], 1], [PROPS.rocksDarkGrey[5], 1]] },        // stony ground
      { on: ['floor'], chance: 0.03, frames: even(PROPS.puddle) },                                                                                                       // the seep
      { on: ['floor'], chance: 0.015, frames: even([PROPS.crystals.grey[0], PROPS.crystals.orange]) },
      { on: ['floor'], chance: 0.01, frames: even([...PROPS.bones, PROPS.bonePiles[0]]) },
      { on: ['water'], chance: 0.04, frames: even(PROPS.lilyPad), layer: 'props' },
    ],
  },
  // docs/23 Y10: the Ashlands' open country, drawn with forest layouts like the outdoor kits: black
  // rock underfoot, crags of dark and red stone with dead trees for its walls, orange crystal for the
  // glowing vents, and its water lava (the rules read the Ashlands' water as lava, field.rs Land::Ashlands)
  ashlands: {
    key: 'ashlands', name: 'The Ashlands (lava and black rock)',
    base: one(FILLS.greyRock),
    floor: darkFloor(DUNGEON_ROWS.darkGrey!),
    rough: rubble([PROPS.rocks, PROPS.boulder, PROPS.crystals.orange, ...PROPS.rocksDarkGrey.slice(0, 3)]),   // cinders and scoria
    water: { set: LIQUID.lava!, animate: true },
    wall: {
      kind: 'props', frames: [[PROPS.rocksDarkGrey[0], 3], [PROPS.rocksDarkGrey[2], 3], [PROPS.rocksDarkGrey[4], 3], [PROPS.rocksRed[0], 2], [PROPS.boulder, 2], [PROPS.trees.dead, 2]],
      groves: [
        { patch: { size: 3, share: 0.25 }, frames: [[PROPS.trees.dead, 4], [PROPS.rocksDarkGrey[1], 2], [PROPS.trees.stump, 1]] },                  // a burnt stand
        { patch: { size: 3, share: 0.15 }, frames: [[PROPS.rocksRed[1], 2], [PROPS.rocksRed[3], 2], [PROPS.crystals.orange, 2]] },                   // a vent field
      ],
    },
    rock: one(FILLS.greyRock),
    thicket: [[PROPS.roundBushBrown[0], 3], [PROPS.roundBushBrown[1], 3], [PROPS.trees.bramble[2], 1]],
    rockAsWall: true,
    door: { closed: PROPS.doors.boarded[1], open: PROPS.doors.archOpen },
    shadows: true,
    decals: [
      { on: ['floor'], chance: 0.3, cluster: { size: 3, share: 0.22 }, frames: [[PROPS.rocks, 2], [PROPS.rocksDarkGrey[3], 1], [PROPS.rocksDarkGrey[5], 1]] },   // cinder fields
      { on: ['floor'], chance: 0.025, frames: even([PROPS.crystals.orange, PROPS.rocksRed[2]]) },                                                                // glowing vents
      { on: ['floor'], chance: 0.012, frames: even([...PROPS.bones, PROPS.bonePiles[0], PROPS.hornedSkull]) },
      { on: ['floor'], chance: 0.01, frames: even([PROPS.trees.stump, PROPS.boulder]) },
    ],
  },
};

export const DEFAULT_KIT = 'keep';

/**
 * The place kits of the commissioned art (docs/186) have no Oryx kit: each is tiled and lit as its nearest one. An
 * outdoor place stands in its country (`board.country`, EA14; `standIn` without one), an indoor one in a hall.
 */
export const PLACE_KITS: Readonly<Record<string, { outdoor: boolean; standIn: string }>> = {
  town: { outdoor: true, standIn: 'forest' }, docks: { outdoor: true, standIn: 'forest' }, courtyard: { outdoor: true, standIn: 'forest' },
  graveyard: { outdoor: true, standIn: 'forest' }, ruins: { outdoor: true, standIn: 'forest' },
  house: { outdoor: false, standIn: 'brick' }, ship: { outdoor: false, standIn: 'brick' }, barrow: { outdoor: false, standIn: 'tomb' }, mine: { outdoor: false, standIn: 'cavern' },
};
export function kitFor(key: string | undefined): Kit { return (key && (KITS[key] ?? KITS[PLACE_KITS[key]?.standIn ?? ''])) || KITS[DEFAULT_KIT]!; }
/** The Oryx kit a board is tiled and lit as: an outdoor place's country when the stage names one, else its own kit key. */
export function oryxKeyOf(board: { kit?: string; country?: string }): string | undefined {
  const place = board.kit ? PLACE_KITS[board.kit] : undefined;
  return place?.outdoor && board.country && KITS[board.country] ? board.country : board.kit;
}

/** A guess for boards that carry no kit key yet (live fixtures): mire country vs stone. */
export function inferKit(board: { tiles: ReadonlyArray<{ terrain: string } | undefined> }): string {
  let water = 0, bad = 0, wall = 0, n = 0;
  for (const t of board.tiles) { if (!t || t.terrain === 'Void') continue; n++; if (t.terrain === 'Water') water++; else if (t.terrain === 'Bad') bad++; else if (t.terrain === 'Wall') wall++; }
  if (n === 0) return DEFAULT_KIT;
  if (water + bad > n * 0.2 && wall < n * 0.25) return 'mirewood';
  return 'cistern';
}
